import {
  getDemoCategoryBreakdown,
  getDemoBudgets,
  getDemoExpenses,
  getDemoIncome,
} from "../data/demoData";

export const DEMO_STORAGE_EVENT = "finora-demo-storage-updated";

const DEMO_EXPENSES_KEY = "demo_expenses";
const DEMO_INCOME_KEY = "demo_income";
const DEMO_BUDGETS_KEY = "demo_budgets";

const resettableDemoKeys = [
  DEMO_EXPENSES_KEY,
  DEMO_INCOME_KEY,
  DEMO_BUDGETS_KEY,
  "demo_predictions",
  "demo_ai_insights",
  "demo_ocr_history",
  "demo_reports",
  "demo_activity",
  "demo_monthly_comparison",
];

const now = new Date();

const readJson = (key) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const writeJson = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

const notifyDemoStorageUpdated = () => {
  window.dispatchEvent(new Event(DEMO_STORAGE_EVENT));
};

const sameMonthYear = (item, month, year) =>
  Number(item.month) === Number(month) && Number(item.year) === Number(year);

export function initializeDemoData(month = now.getMonth() + 1, year = now.getFullYear()) {
  if (!Array.isArray(readJson(DEMO_EXPENSES_KEY))) {
    writeJson(DEMO_EXPENSES_KEY, getDemoExpenses(month, year));
  }

  if (!Array.isArray(readJson(DEMO_INCOME_KEY))) {
    writeJson(DEMO_INCOME_KEY, getDemoIncome(month, year));
  }

  if (!Array.isArray(readJson(DEMO_BUDGETS_KEY))) {
    writeJson(DEMO_BUDGETS_KEY, getDemoBudgets(month, year));
  }
}

export function resetDemoData(month = now.getMonth() + 1, year = now.getFullYear()) {
  const storageKeys = [];

  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);

    if (key) {
      storageKeys.push(key);
    }
  }

  storageKeys.forEach((key) => {
    if (key.startsWith("demo_")) {
      localStorage.removeItem(key);
    }
  });

  resettableDemoKeys.forEach((key) => localStorage.removeItem(key));
  writeJson(DEMO_EXPENSES_KEY, getDemoExpenses(month, year));
  writeJson(DEMO_INCOME_KEY, getDemoIncome(month, year));
  writeJson(DEMO_BUDGETS_KEY, getDemoBudgets(month, year));
  notifyDemoStorageUpdated();
}

export function getStoredDemoExpenses(month, year) {
  initializeDemoData(month, year);
  return (readJson(DEMO_EXPENSES_KEY) || []).filter((expense) =>
    sameMonthYear(expense, month, year)
  );
}

export function getStoredDemoIncome(month, year) {
  initializeDemoData(month, year);
  return (readJson(DEMO_INCOME_KEY) || []).filter((income) =>
    sameMonthYear(income, month, year)
  );
}

export function getStoredDemoBudgets(month, year) {
  initializeDemoData(month, year);
  return (readJson(DEMO_BUDGETS_KEY) || []).filter((budget) =>
    sameMonthYear(budget, month, year)
  );
}

export function addStoredDemoExpenses(expenseItems, month, year) {
  initializeDemoData(month, year);
  const current = readJson(DEMO_EXPENSES_KEY) || [];
  const createdAt = new Date().toISOString();
  const nextExpenses = expenseItems.map((expense, index) => ({
    _id: `demo-expense-${Date.now()}-${index}`,
    text: expense.text || expense.description || "Demo expense",
    amount: Number(expense.amount || 0),
    category: expense.category || "others",
    source: expense.source || "rule",
    month: Number(month),
    year: Number(year),
    createdAt,
  }));

  writeJson(DEMO_EXPENSES_KEY, [...nextExpenses, ...current]);
  notifyDemoStorageUpdated();
  return nextExpenses;
}

export function deleteStoredDemoExpense(id) {
  const current = readJson(DEMO_EXPENSES_KEY) || [];
  writeJson(
    DEMO_EXPENSES_KEY,
    current.filter((expense) => expense._id !== id)
  );
  notifyDemoStorageUpdated();
}

export function replaceStoredDemoExpenses(month, year, expenses) {
  initializeDemoData(month, year);
  const current = readJson(DEMO_EXPENSES_KEY) || [];
  const otherMonths = current.filter((expense) => !sameMonthYear(expense, month, year));
  writeJson(DEMO_EXPENSES_KEY, [...expenses, ...otherMonths]);
  notifyDemoStorageUpdated();
}

export function addStoredDemoIncome(income, month, year) {
  initializeDemoData(month, year);
  const current = readJson(DEMO_INCOME_KEY) || [];
  const nextIncome = {
    _id: `demo-income-${Date.now()}`,
    source: income.source || "Income",
    amount: Number(income.amount || 0),
    note: income.note || "",
    month: Number(month),
    year: Number(year),
    createdAt: new Date().toISOString(),
  };

  writeJson(DEMO_INCOME_KEY, [nextIncome, ...current]);
  notifyDemoStorageUpdated();
  return nextIncome;
}

export function deleteStoredDemoIncome(id) {
  const current = readJson(DEMO_INCOME_KEY) || [];
  writeJson(
    DEMO_INCOME_KEY,
    current.filter((income) => income._id !== id)
  );
  notifyDemoStorageUpdated();
}

export function upsertStoredDemoBudget(category, amount, month, year) {
  initializeDemoData(month, year);
  const current = readJson(DEMO_BUDGETS_KEY) || [];
  const normalizedCategory = String(category || "others").toLowerCase();
  const existingIndex = current.findIndex(
    (budget) => sameMonthYear(budget, month, year) && budget.category === normalizedCategory
  );
  const nextBudget = {
    _id:
      existingIndex >= 0
        ? current[existingIndex]._id
        : `demo-budget-${Date.now()}`,
    category: normalizedCategory,
    amount: Number(amount || 0),
    month: Number(month),
    year: Number(year),
    createdAt: existingIndex >= 0 ? current[existingIndex].createdAt : new Date().toISOString(),
  };
  const nextBudgets =
    existingIndex >= 0
      ? current.map((budget, index) => (index === existingIndex ? nextBudget : budget))
      : [nextBudget, ...current];

  writeJson(DEMO_BUDGETS_KEY, nextBudgets);
  notifyDemoStorageUpdated();
  return nextBudget;
}

export function getStoredDemoDashboardData(month, year) {
  const expenses = getStoredDemoExpenses(month, year);
  const incomes = getStoredDemoIncome(month, year);
  const budgets = getStoredDemoBudgets(month, year);
  const totalSpend = expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
  const categoryBreakdown = getDemoCategoryBreakdown(expenses);
  const topCategoryEntry = Object.entries(categoryBreakdown).sort((a, b) => b[1] - a[1])[0];
  const previousTotal = 92500;
  const predictedTotal = 106000;

  return {
    expenses,
    incomes,
    budgets,
    prediction: {
      predictedTotal,
      averageMonthlySpend: 98000,
      highestCategory: topCategoryEntry?.[0] || "rent",
      trend: "stable",
      message: "Demo forecast expects next month around Rs 1,06,000. Watch rent, grocery, and shopping.",
      categoryPrediction: {
        rent: 32000,
        food: 9000,
        transport: 7600,
        grocery: 14500,
        shopping: 10500,
        utilities: 6500,
        entertainment: 4500,
        medical: 6000,
        education: 7800,
        beverage: 3500,
        others: 2600,
      },
    },
    monthlyComparison: {
      selectedMonth: Number(month),
      selectedYear: Number(year),
      previousMonth: Number(month) === 1 ? 12 : Number(month) - 1,
      previousYear: Number(month) === 1 ? Number(year) - 1 : Number(year),
      currentTotal: totalSpend,
      previousTotal,
      difference: totalSpend - previousTotal,
      percentageChange: ((totalSpend - previousTotal) / previousTotal) * 100,
      trend: totalSpend > previousTotal ? "increased" : "decreased",
      message: "Demo spending is higher than last month because grocery and shopping increased.",
    },
    aiInsights: {
      totalSpend,
      topCategory: topCategoryEntry?.[0] || "rent",
      topAmount: topCategoryEntry?.[1] || 0,
      expenseCount: expenses.length,
      categoryBreakdown,
      insight:
        "Demo Mode: fixed rent is under control, grocery is near budget, and shopping is slightly above plan.",
    },
  };
}
